import { Platform } from 'react-native';

import actions from '../../../../actions';
import { VERSION } from '../../../../config';
import {
    getDeviceModel,
    getDeviceName,
    getUniqueDeviceId
} from '../../../../services/device';
import {
    FIRST_AID_KIT_KEY,
    getFirstAidKitFromStorage,
    saveFirstAidKit
} from '../../../../services/firstAidKit';
import { storage } from '../../../../services/localstorage';
import {
    getMyDocumentsFromStorage,
    setMyDocumentsToStorage
} from '../../../../services/myDocuments';
import { PROFILES_KEY } from '../../../../services/user.utils';
import store from '../../../../store';
import {
    decrypt,
    encrypt,
    sortBy
} from '../../../../utils';
import { consoleLog } from '../../../../utils/log';
import { mergeArrays } from '../../../../utils/mergeArrays';
import {
    decryptVisits,
    encryptVisits,
    FB_SYNC_KEY,
    PAST_VISITS_KEY
} from '../../../App/snippets/Tests&Visits/utils';
import { mergeObjectArraysByKey } from '../util';
import { LOG_RESTORE } from './backupRestore';
import { getAttachmentsForBackup } from './visitAttachments';

export const KEY_LAST_BACKUP_STATUS = 'lastBackupStatus';
export const KEY_LAST_SUCCESS_DATE = 'lastBackupSuccessDate';
export const KEY_LAST_ERROR_DATE = 'lastBackupErrorDate';
export const KEY_STATE_HISTORY = 'lastBackupStateHistory';

export const BACKUP_DRUG_SEARCH = false;

export const KEY_BACKUP_STATE_HISTORY_LENGTH = 'backupStateHistoryLength';

export const KEY_DELETED_ATTACHES = 'deletedAttaches';

const STATE_HISTORY_LENGTH = 5;

export const CLEAR_DELETED_ATTACHES_AFTER_BACKUP = false;
// eslint-disable-next-line no-console
const toLog = (...args) => {
    if (Object.commonLog !== false) {
        actions.addLog(...args);
        console.log(`%c${args?.[0] ?? ''}`, 'color: #000099', ...args);
    }
};

export const getCurrentState = () => store?.getState?.() || {};

export const getDeletedAttaches = async () => {
    try {

        return await storage.getForProfile(KEY_DELETED_ATTACHES, []);
        // return Array.isArray(deletedAttachesData) ? deletedAttachesData?.filter?.(b => Boolean(b)) : [];
    } catch (e) {
        return [];
    }
};
export const addToDeletedAttaches = async (file) => {
    try {
        toLog('deleteAttachments:list before', file);
        let deletedAttaches = await getDeletedAttaches();
        toLog('deleteAttachments:list', { deletedAttaches });
        if (Array.isArray(file)) {
            deletedAttaches = deletedAttaches.concat(file);
        } else {
            deletedAttaches = deletedAttaches.concat([ file ]);
        }
        deletedAttaches = Array.from(new Set(deletedAttaches));
        toLog('deleteAttachments:ready', deletedAttaches);
        await storage.setForProfile(KEY_DELETED_ATTACHES, deletedAttaches);
        return file;
    } catch (e) {
        console.log('deleteAttachments:error', e);
        return null;
    }
};

export const clearDeletedAttaches = async () => storage.setForProfile(KEY_DELETED_ATTACHES, []);

export const getBackupEnabled = () => {
    const state = getCurrentState();
    const prefs = state?.prefs?.data || {};
    const googleCloudEnabled = Platform.OS !== 'web' && (prefs?.googleCloudEnabled ?? true);
    const iCloudEnabled = Platform.OS === 'ios' && (prefs?.iCloudEnabled ?? true);

    return {
        googleCloudEnabled,
        iCloudEnabled,
    };

};

export const getProfileInfo = () => {
    const state = getCurrentState();
    const {
        user: {
            info,
        } = {},
    } = state || {};
    return {
        profileId: info?.me?.profileId,
        hashKey: info?.hashKey,
        storeKey: info?.storeKey,
        info,
    };
};

export const getVisits = async () => {
    const state = getCurrentState();
    let pastVisits = state?.pastVisits?.data || [];

    if (!pastVisits || !pastVisits?.length) {
        const v = (await storage.get(PAST_VISITS_KEY)) ?? [];
        const hashKey = state?.user?.info?.hashKey || '';
        pastVisits = decryptVisits(v, hashKey);
    }

    return pastVisits;
};

export const getFamilyForBackup = () => {
    const state = getCurrentState();
    const { relatives = [] } = state?.user?.info || {};
    return relatives?.filter(({ relKind }) => relKind === 'CHILD') || [];
};

export const getFavoritesForBackup = () => {
    const state = getCurrentState();
    const { fullFavorites = [] } = state?.prefs?.data || {};
    return fullFavorites;
};

export const getDrugsSearchForBackup = () => {
    try {
        const state = getCurrentState();
        const { drugsSearch = [] } = state?.prefs?.data || {};
        return drugsSearch;
    } catch {
        return [];
    }
};

export const getBasketForBackup = () => {
    try {
        const state = getCurrentState();
        const { basket = [] } = state?.prefs?.data || {};
        return basket;
    } catch {
        return [];
    }
};

export const getDocuments = async () => {
    try {
        const documents = await getMyDocumentsFromStorage();
        return documents ?? [];
    } catch (e) {
        toLog('error', 'Error get documents for backup', e);
        return [];
    }
};

export const getFullBackupData = async ({ disableLog = false } = {}) => {
    const visits = await getVisits();
    const family = getFamilyForBackup();
    const favorites = getFavoritesForBackup();
    const attachments = await getAttachmentsForBackup();
    const basket = getBasketForBackup();
    const documents = await getDocuments();
    const drugsSearch = BACKUP_DRUG_SEARCH ? getDrugsSearchForBackup() : undefined;

    const state = getCurrentState();
    const {
        hashKey,
        me: { profileId: profileId } = {},
    } = state?.user?.info || {};

    const deviceId = await getUniqueDeviceId();
    const deviceName = await getDeviceName();
    const deviceModel = getDeviceModel();

    const data = {
        visits,
        family,
        favorites,
        basket,
        drugsSearch,
        deviceId,
        deviceName,
        deviceModel,
        documents,
    };

    let encryptedData = '';
    if (hashKey) {
        try {
            const stringData = JSON.stringify(data);
            encryptedData = encrypt(stringData, hashKey);
        } catch (error) {
            toLog('Error deserialize backup data:', error);
        }
    }

    data.attachments = attachments || [];
    !disableLog && toLog('Full backup data:', data);

    return {
        data,
        profileId,
        hashKey,
        encryptedData,
        backupData: {
            encryptedData,
            version: VERSION,
            profileId,
        },
    };
};

export const getBackupHistoryLength = async () => {
    const value = await storage.get(KEY_BACKUP_STATE_HISTORY_LENGTH);
    return value ?? STATE_HISTORY_LENGTH;
};

export const setBackupHistoryLength = async (value) => await storage.set(KEY_BACKUP_STATE_HISTORY_LENGTH, value);

export const setLastBackupState = async (data) => {
    const historyLength = await getBackupHistoryLength();
    if (data?.state === 'success') {
        await storage.set(KEY_LAST_SUCCESS_DATE, Date.now());
    } else if (data?.state === 'error') {
        await storage.set(KEY_LAST_ERROR_DATE, Date.now());
    }
    const history = (await storage.get(KEY_STATE_HISTORY)) ?? [];
    const h = Array.isArray(history) ? history : [];
    h.push({
        state: data?.state,
        date: Date.now(),
    });
    const historyState = sortBy(h, 'date', -1)
        ?.slice
        ?.(0, historyLength) ?? [];
    await storage.set(KEY_STATE_HISTORY, historyState);
    await storage.set(KEY_LAST_BACKUP_STATUS, data);
    actions.setFormValue('lastBackupState', data);
    actions.setFormValue(KEY_STATE_HISTORY, historyState);
};

export const getLastBackupState = async () => {
    return (await storage.get(KEY_LAST_BACKUP_STATUS)) ?? '';
};

export const getBackupHistory = async () => {
    const history = (await storage.get(KEY_STATE_HISTORY)) ?? [];
    return sortBy(Array.isArray(history) ? history : [], 'date', -1);
};

export const getLastBackupDates = async () => {
    const lastSuccessDate = await storage.get(KEY_LAST_SUCCESS_DATE);
    const lastErrorDate = await storage.get(KEY_LAST_ERROR_DATE);

    return {
        success: lastSuccessDate ? new Date(lastSuccessDate) : null,
        error: lastErrorDate ? new Date(lastErrorDate) : null,
        history: await getBackupHistory(),
    };
};

export const loadBackupStates = async () => {
    const history = await getBackupHistory();
    actions.setFormValue(KEY_STATE_HISTORY, history);
    const dates = await getLastBackupDates();
    actions.setFormValue(KEY_LAST_SUCCESS_DATE, dates.success);
    actions.setFormValue(KEY_LAST_ERROR_DATE, dates.error);
    return {
        dates,
        history,
    };
};

export const removeBackupHistory = async () => {
    await storage.set(KEY_STATE_HISTORY, []);
    actions.setFormValue(KEY_STATE_HISTORY, []);
};

const restoreFamily = async ({
    relatives: familyToSave,
    profileId,
    hashKey,
}) => {
    if (!profileId || !hashKey) {
        return false;
    }
    try {

        let profileList = (await storage.get(PROFILES_KEY, [])) ?? [];

        if (typeof profileList !== 'object') {
            profileList = [];
        } else if (!Array.isArray(profileList)) {
            profileList = [];
        }

        let foundIndex = -1;
        let currentProfile = {};
        profileList.some((item, idx) => {
            const decrypted = decrypt(item ?? '', hashKey);
            if (decrypted) {
                foundIndex = idx;
                currentProfile = decrypted;
                return true;
            }
            return false;
        });

        familyToSave.forEach((item) => {
            currentProfile[item?.profileId] = {
                profileId: item?.profileId,
                fullNameRu: item?.fullNameRu,
                birthday: item?.birthday,
                sex: item?.sex,
                phones: item?.phones ?? [],
            };
        });

        const encrypted = encrypt(currentProfile, hashKey);

        if (foundIndex >= 0) {
            profileList[foundIndex] = encrypted;
        } else {
            profileList.push(encrypted);
        }

        await storage.set(PROFILES_KEY, profileList);
        return true;
    } catch (e) {
        return false;
    }

};
const restoreVisits = async ({ visits }) => {
    LOG_RESTORE && consoleLog('restoreVisits', visits);
    // await updatePastVisits(visits);
    if (!visits?.length) {
        actions.stopLoadingPastVisits();
        return;
    }
    const currentPVisits = decryptVisits(await storage.get(PAST_VISITS_KEY));
    for (const visit of visits) {
        const found = currentPVisits?.findIndex((v) => v.id === visit.id);
        if (found >= 0) {
            const currentVisit = currentPVisits[found]; //name, comment, drugs, attachments
            if (!currentVisit?.name && visit?.name) {
                currentVisit.name = visit.name;
            }
            if (!currentVisit?.comment && visit?.comment) {
                currentVisit.comment = visit.comment;
            }
            if (visit?.drugs && Array.isArray(visit?.drugs) && visit?.drugs?.length) {
                currentVisit.drugs = mergeArrays(currentVisit?.drugs ?? [], visit?.drugs, 'ls_num');
            }
            if (visit?.attachments && Array.isArray(visit?.attachments) && visit?.attachments) {
                currentVisit.attachments = mergeArrays(currentVisit?.attachments ?? [], visit?.attachments, 'path');
            }

        } else {
            currentPVisits?.push(visit);
        }
    }
    const result = (currentPVisits?.length ? mergeArrays(currentPVisits, visits ?? [], 'id') : visits);
    LOG_RESTORE && consoleLog('currentPVisits', {
        currentPVisits,
        visits,
        result,
    });
    const enc = encryptVisits(result) ?? [];
    await storage.set(PAST_VISITS_KEY, enc);
    storage
        .get(FB_SYNC_KEY)
        .then((d) => {
            const allData = typeof d === 'object' && d !== null ? { ...d } : {};
            allData[store?.getState?.()?.user?.info?.me?.profileId] = enc;
            storage.set(FB_SYNC_KEY, allData)
                .catch((err) => {
                    console.log(`error set ${FB_SYNC_KEY}`, { err });
                });
        })
        .catch((e) => {
            console.log(`error get ${FB_SYNC_KEY}`, { e });
        });
    actions.setPurePastVisits(result);

};
const restoreFavorites = async ({ favorites }) => {
    for (const favorite of favorites) {
        await actions.addFullFavorites(favorite);
    }
};

const restoreBasket = async ({
    basket,
    drugSearch,
    profileId,
    hashKey,
}) => {
    LOG_RESTORE && toLog('restoreBasket', {
        basket,
        drugSearch,
        profileId,
        hashKey,
    });
    const { basket: currentBasket = [] } = await getFirstAidKitFromStorage({
        profileId,
        hashKey,
    });
    const allData = (await storage.getObject(FIRST_AID_KIT_KEY, {})) ?? {};
    const allDataDecoded = decrypt(allData?.[profileId] || '', hashKey) || {};
    const drugSearchStored = allDataDecoded?.drugSearch || [];
    const drugSearchOut = BACKUP_DRUG_SEARCH ? drugSearch : drugSearchStored;
    await saveFirstAidKit({
        profileId,
        hashKey,
        basket: mergeArrays(currentBasket, basket ?? [], 'ls_num'),
        drugSearch: drugSearchOut,
    });
};

export const restoreMyDocuments = async ({
    documents,
    profileId,
    hashKey,
}) => {
    if (!Array.isArray(documents) || !documents?.length) {
        toLog('Nothing to restore for My Documents');
        return;
    }
    const currentDocs = await getMyDocumentsFromStorage({
        profileId,
        hashKey,
    }) ?? [];
    const docsToSave = mergeObjectArraysByKey(currentDocs, documents, 'id');
    await setMyDocumentsToStorage({
        documents: docsToSave,
        profileId,
        hashKey,
    });
    toLog('My Documents restored:', { docsToSave });
};

// eslint-disable-next-line max-statements
export const restoreData = async ({
    file,
    profileId,
    hashKey,
    restore = true,
}) => {
    if (file?.profileId !== profileId) {
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveRestoreProfileMismatch'));
        throw new Error(Object.R('errors.googleDriveRestoreProfileMismatch'));
    }

    if (!file?.encryptedData) {
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveRestoreNoData'));
        throw new Error(Object.R('errors.googleDriveRestoreNoData'));
    }

    let data = null;
    let decrypted = null;
    try {
        decrypted = decrypt(file?.encryptedData, hashKey);
    } catch (e) {
        toLog('Restore error', 'Error decrypting backup data:', e);
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveRestoreDecryptError', { addInfo: e?.message ?? '' }));
        throw new Error(Object.R('errors.googleDriveRestoreDecryptError', { addInfo: e?.message ?? '' }));
    }
    try {
        data = JSON.parse(decrypted);
    } catch (e) {
        toLog('Restore', 'Error decrypting backup data:', e);
        throw new Error(Object.R('errors.googleDriveRestoreDecryptError', { addInfo: e?.message ?? '' }));
    }
    LOG_RESTORE && toLog('Restore', 'Decrypted backup data:', {
        data,
        file,
    });
    if (!data) {
        actions.setFormValue('googleBackupProcessMessages', Object.R('errors.googleDriveRestoreNoData'));
        toLog('Restore', 'Error decrypting backup data', data);
        throw new Error(Object.R('errors.googleDriveRestoreNoData'));
    }

    LOG_RESTORE && toLog(`DATA FOR RESTORE: ${restore}`, data);
    if (data?.family && Array.isArray(data?.family) && restore) {
        await restoreFamily({
            profileId,
            hashKey,
            relatives: data.family,
        });
    }

    if (data?.documents && Array.isArray(data?.documents) && restore) {
        await restoreMyDocuments({
            documents: data.documents,
            profileId,
            hashKey,
        });
    }

    if (data?.visits && Array.isArray(data?.visits) && restore) {
        await restoreVisits({ visits: data.visits });
    }

    if (data?.favorites && Array.isArray(data?.favorites) && restore) {
        await restoreFavorites({ favorites: data.favorites });
    }

    if (data?.basket && Array.isArray(data?.basket) && restore) {
        await restoreBasket({
            basket: data?.basket ?? [],
            drugSearch: data?.drugsSearch ?? [],
            profileId,
            hashKey,
        });
    }

    await actions.reloadUserInfo(Object.handleNewUserItemsFound, actions.updatePrefs, actions.updateSelects);

    return data;
};
